// Cotizaciones / Proformas. Financiero → Ventas y Cobros → Cotizaciones.
// NO es un documento del SRI: no se firma ni se emite, no genera asiento ni entra
// a la cartera. Es la oferta comercial previa a la venta; al aceptarse se
// convierte en factura (que nace 'por emitir' y sigue el camino normal).
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, Pencil, Plus, Trash2, FileText, Send, Check, X, CalendarPlus, ArrowRightCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { fechaLocal } from '@/lib/utils/fecha';
import { useTabsStore } from '@/stores/tabs.store';
import { labelCentroCosto, useCentrosContables, useCentroCostoModo } from './centroCosto';
import { abrirCotizacionPdf } from './cotizacionPdf';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const field = 'flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const usd = (v: number) => (Number(v) || 0).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const n2 = (v: number) => (Number.isFinite(v) ? v : 0);
const TARIFAS = [0, 5, 12, 14, 15];

const ESTADO: Record<string, { label: string; cls: string }> = {
  borrador:   { label: 'Borrador',   cls: 'bg-muted text-muted-foreground' },
  enviada:    { label: 'Enviada',    cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  aceptada:   { label: 'Aceptada',   cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  rechazada:  { label: 'Rechazada',  cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  convertida: { label: 'Facturada',  cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300' },
};

interface PartnerOpt { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[] }
interface ProductoOpt { id: string; codigo: string; nombre: string; iva_tarifa: number; precio_base: number }
interface Linea {
  key: number; producto_id: string; codigo: string; descripcion: string;
  cantidad: number; precio_unitario: number; descuento: number; iva_tarifa: number;
}
interface CotRow {
  id: string; numero: string; fecha: string; fecha_validez: string; dias_validez: number;
  identificacion: string; razon_social: string; email: string | null; estado: string;
  subtotal: number; iva: number; importe_total: number; lineas: number; vencida: boolean;
  motivo_rechazo: string | null; factura_ticket_id: string | null;
  factura_serie: string | null; factura_estado: string | null;
}
interface Resumen {
  abiertas: number; vencidas: number; aceptadas: number; rechazadas: number; convertidas: number;
  monto_abierto: number; monto_convertido: number; efectividad: number | null;
}

let _k = 1;
const nuevaLinea = (): Linea => ({
  key: _k++, producto_id: '', codigo: '', descripcion: '', cantidad: 1, precio_unitario: 0, descuento: 0, iva_tarifa: 15,
});
// El descuento es POR UNIDAD (igual que en la factura), así la conversión no mueve totales.
const baseLinea = (l: Linea) => Math.max(n2(l.cantidad) * (n2(l.precio_unitario) - n2(l.descuento)), 0);
const ivaLinea = (l: Linea) => Math.round(baseLinea(l) * n2(l.iva_tarifa)) / 100;

export function Cotizaciones() {
  const qc = useQueryClient();
  const [vista, setVista] = useState<'lista' | 'nueva'>('lista');
  const [editId, setEditId] = useState<string | null>(null);
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Cotizaciones</h1>
          <p className="text-sm text-muted-foreground">
            La oferta que le mandas al cliente antes de facturar. Al aceptarse se convierte en factura.
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border p-1">
          {([['nueva', editId ? 'Editar' : 'Nueva'], ['lista', 'Cotizaciones']] as const).map(([k, t]) => (
            <button key={k} onClick={() => { if (k === 'nueva' && vista === 'lista') setEditId(null); setVista(k); }}
              className={`rounded-md px-3 py-1 text-sm ${vista === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{t}</button>
          ))}
        </div>
      </div>
      {vista === 'nueva'
        ? <Editor editId={editId} onGuardada={() => { setEditId(null); setVista('lista'); void qc.invalidateQueries({ queryKey: ['cot'] }); }}
                  onCancelar={() => { setEditId(null); setVista('lista'); }} />
        : <Lista onEdit={(id) => { setEditId(id); setVista('nueva'); }} onNueva={() => { setEditId(null); setVista('nueva'); }} />}
    </div>
  );
}

function Editor({ editId, onGuardada, onCancelar }: { editId: string | null; onGuardada: () => void; onCancelar: () => void }) {
  const { data: partners = [] } = useQuery<PartnerOpt[]>({
    queryKey: ['fin-partners'], queryFn: async () => (await apiClient.post('/rpc/sri_partners', {}, FIN)).data || [],
  });
  const clientes = useMemo(
    () => partners.filter((p) => ((p.tipo_partner || []).includes('cliente') || (p.tipo_partner || []).includes('paciente')) && p.identificacion),
    [partners]);
  const centros = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('centro_costo')), [partners]);
  const { modo: modoCentro } = useCentroCostoModo();
  const { data: centrosContables = [] } = useCentrosContables(modoCentro === 'contable');
  const labelCentro = labelCentroCosto(modoCentro);
  // Catálogo completo de vendibles: una cotización no se limita a servicios.
  const { data: productos = [] } = useQuery<ProductoOpt[]>({
    queryKey: ['inv-vendibles-cot'],
    queryFn: async () => (await apiClient.get('/rest/inventario.productos?select=id,codigo,nombre,iva_tarifa,precio_base&es_vendible=eq.true&es_activo=eq.true&order=nombre&limit=2000')).data || [],
  });

  const [ident, setIdent] = useState('');
  const [razon, setRazon] = useState('');
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [fecha, setFecha] = useState(fechaLocal());
  const [dias, setDias] = useState(15);
  const [centroId, setCentroId] = useState('');
  const [proyectoId, setProyectoId] = useState('');
  const [condiciones, setCondiciones] = useState('');
  const [obs, setObs] = useState('');
  const [lineas, setLineas] = useState<Linea[]>([nuevaLinea()]);

  const alCambiarIdent = (v: string) => {
    setIdent(v);
    const p = clientes.find((x) => x.identificacion === v.trim());
    if (p) setRazon(p.razon_social);
  };

  const { data: edit } = useQuery<Record<string, unknown> | null>({
    queryKey: ['cot-obtener', editId], enabled: !!editId,
    queryFn: async () => (await apiClient.post('/rpc/cot_obtener', { _id: editId }, FIN)).data,
  });
  useEffect(() => {
    if (!edit) return;
    const e = edit as {
      identificacion?: string; razon_social?: string; email?: string; telefono?: string;
      fecha?: string; dias_validez?: number; centro_costo_id?: string; cc_contable_id?: string;
      proyecto_id?: string; condiciones?: string; observaciones?: string;
      lineas?: { producto_id?: string; codigo?: string; descripcion?: string; cantidad?: number; precio_unitario?: number; descuento?: number; iva_tarifa?: number }[];
    };
    setIdent(e.identificacion || ''); setRazon(e.razon_social || '');
    setEmail(e.email || ''); setTelefono(e.telefono || '');
    setFecha(e.fecha ? String(e.fecha).slice(0, 10) : fechaLocal());
    setDias(Number(e.dias_validez) ?? 15);
    setCentroId((modoCentro === 'contable' ? e.cc_contable_id : e.centro_costo_id) || '');
    setProyectoId(e.proyecto_id || '');
    setCondiciones(e.condiciones || ''); setObs(e.observaciones || '');
    setLineas((e.lineas || []).map((l) => ({
      key: _k++, producto_id: l.producto_id || '', codigo: l.codigo || '', descripcion: l.descripcion || '',
      cantidad: Number(l.cantidad) || 1, precio_unitario: Number(l.precio_unitario) || 0,
      descuento: Number(l.descuento) || 0, iva_tarifa: Number(l.iva_tarifa) || 0,
    })));
  }, [edit, modoCentro]);

  const setLinea = (key: number, patch: Partial<Linea>) =>
    setLineas((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const elegirProducto = (key: number, pid: string) => {
    const p = productos.find((x) => x.id === pid);
    setLinea(key, p
      ? { producto_id: p.id, codigo: p.codigo, descripcion: p.nombre, precio_unitario: Number(p.precio_base) || 0, iva_tarifa: Number(p.iva_tarifa) || 0 }
      : { producto_id: '' });
  };

  const subtotal = lineas.reduce((s, l) => s + baseLinea(l), 0);
  const descuento = lineas.reduce((s, l) => s + n2(l.cantidad) * n2(l.descuento), 0);
  const iva = lineas.reduce((s, l) => s + ivaLinea(l), 0);
  const total = subtotal + iva;

  const guardar = useMutation({
    mutationFn: async () => {
      const cab = {
        identificacion: ident.trim(), razon_social: razon.trim(),
        email: email.trim() || null, telefono: telefono.trim() || null,
        fecha, dias_validez: dias,
        centro_costo_id: modoCentro === 'contable' ? null : (centroId || null),
        cc_contable_id: modoCentro === 'contable' ? (centroId || null) : null,
        proyecto_id: proyectoId || null,
        condiciones: condiciones.trim() || null, observaciones: obs.trim() || null,
      };
      const items = lineas.map((l) => ({
        producto_id: l.producto_id || null, codigo: l.codigo.trim() || null,
        descripcion: l.descripcion.trim(), cantidad: n2(l.cantidad),
        precio_unitario: n2(l.precio_unitario), descuento: n2(l.descuento), iva_tarifa: n2(l.iva_tarifa),
      }));
      return editId
        ? (await apiClient.post('/rpc/cot_actualizar', { _id: editId, _cab: cab, _items: items }, FIN)).data
        : (await apiClient.post('/rpc/cot_crear', { _cab: cab, _items: items }, FIN)).data;
    },
    onSuccess: (r: { message?: string }) => { toast.success(r?.message || 'Cotización guardada'); onGuardada(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const puede = ident.trim() && razon.trim() && fecha
    && lineas.length > 0 && lineas.every((l) => l.descripcion.trim()) && total > 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Cliente y validez</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Identificación *</label>
            <input className={field} list="cot-cli" value={ident} maxLength={20}
              onChange={(e) => alCambiarIdent(e.target.value)} placeholder="RUC o cédula" />
            <datalist id="cot-cli">
              {clientes.map((p) => <option key={p.id} value={p.identificacion!}>{p.razon_social}</option>)}
            </datalist>
            <p className="mt-1 text-xs text-muted-foreground">Si aún no es cliente, se crea al guardar.</p>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Nombre / razón social *</label>
            <input className={field} value={razon} onChange={(e) => setRazon(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Correo</label>
            <input type="email" className={field} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="para enviarle el PDF" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Teléfono</label>
            <input className={field} value={telefono} onChange={(e) => setTelefono(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Fecha *</label>
            <input type="date" className={field} value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Validez (días)</label>
            <input type="number" min="0" className={field} value={dias} onChange={(e) => setDias(Number(e.target.value))} />
            <p className="mt-1 text-xs text-muted-foreground">
              Vence el {new Date(new Date(fecha + 'T00:00:00').getTime() + dias * 86400000).toLocaleDateString(LOCALE)}
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">{labelCentro}</label>
            <select className={field} value={centroId} onChange={(e) => setCentroId(e.target.value)}>
              <option value="">—</option>
              {modoCentro === 'contable'
                ? centrosContables.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)
                : centros.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Proyecto</label>
            <select className={field} value={proyectoId} onChange={(e) => setProyectoId(e.target.value)}>
              <option value="">—</option>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Condiciones</label>
            <input className={field} value={condiciones} onChange={(e) => setCondiciones(e.target.value)}
              placeholder="50% anticipo · entrega 15 días · garantía 1 año" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Observaciones</label>
            <input className={field} value={obs} onChange={(e) => setObs(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base">Detalle</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setLineas((ls) => [...ls, nuevaLinea()])}>
            <Plus className="w-4 h-4 mr-1" /> Línea
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b">
              <tr>
                <th className="py-2 pr-3 text-left w-10">N°</th>
                <th className="py-2 pr-3 text-left w-44">Producto</th>
                <th className="py-2 pr-3 text-left">Descripción</th>
                <th className="py-2 pr-3 text-right w-24">Cantidad</th>
                <th className="py-2 pr-3 text-right w-28">P. unitario</th>
                <th className="py-2 pr-3 text-right w-28">Desc. x u.</th>
                <th className="py-2 pr-3 text-right w-24">IVA</th>
                <th className="py-2 pr-3 text-right w-28">Total</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {lineas.map((l, i) => (
                <tr key={l.key} className="border-b last:border-0">
                  <td className="py-1 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-1 pr-3">
                    <select className={field} value={l.producto_id} onChange={(e) => elegirProducto(l.key, e.target.value)}>
                      <option value="">— libre —</option>
                      {productos.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
                    </select>
                  </td>
                  <td className="py-1 pr-3"><input className={field} value={l.descripcion} onChange={(e) => setLinea(l.key, { descripcion: e.target.value })} placeholder="Qué se ofrece" /></td>
                  <td className="py-1 pr-3"><input type="number" step="0.01" min="0" className={`${field} text-right`} value={l.cantidad} onChange={(e) => setLinea(l.key, { cantidad: Number(e.target.value) })} /></td>
                  <td className="py-1 pr-3"><input type="number" step="0.01" min="0" className={`${field} text-right`} value={l.precio_unitario} onChange={(e) => setLinea(l.key, { precio_unitario: Number(e.target.value) })} /></td>
                  <td className="py-1 pr-3"><input type="number" step="0.01" min="0" className={`${field} text-right`} value={l.descuento} onChange={(e) => setLinea(l.key, { descuento: Number(e.target.value) })} /></td>
                  <td className="py-1 pr-3">
                    <select className={`${field} text-right`} value={l.iva_tarifa} onChange={(e) => setLinea(l.key, { iva_tarifa: Number(e.target.value) })}>
                      {TARIFAS.map((t) => <option key={t} value={t}>{t}%</option>)}
                    </select>
                  </td>
                  <td className="py-1 pr-3 text-right tabular-nums">{(baseLinea(l) + ivaLinea(l)).toFixed(2)}</td>
                  <td className="py-1">
                    <button className="text-muted-foreground hover:text-destructive disabled:opacity-30" disabled={lineas.length === 1}
                      onClick={() => setLineas((ls) => ls.filter((x) => x.key !== l.key))}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t font-medium">
              <tr><td colSpan={7} className="py-2 pr-3 text-right text-muted-foreground">Subtotal</td><td className="py-2 pr-3 text-right tabular-nums">{subtotal.toFixed(2)}</td><td /></tr>
              {descuento > 0 && <tr><td colSpan={7} className="py-1 pr-3 text-right text-muted-foreground">Descuento</td><td className="py-1 pr-3 text-right tabular-nums">−{descuento.toFixed(2)}</td><td /></tr>}
              <tr><td colSpan={7} className="py-1 pr-3 text-right text-muted-foreground">IVA</td><td className="py-1 pr-3 text-right tabular-nums">{iva.toFixed(2)}</td><td /></tr>
              <tr className="text-base"><td colSpan={7} className="py-2 pr-3 text-right">Total</td><td className="py-2 pr-3 text-right tabular-nums">{usd(total)}</td><td /></tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancelar}>Cancelar</Button>
        <Button isLoading={guardar.isPending} disabled={!puede} onClick={() => guardar.mutate()}>
          {editId ? 'Guardar cambios' : 'Crear cotización'}
        </Button>
      </div>
    </div>
  );
}

function Lista({ onEdit, onNueva }: { onEdit: (id: string) => void; onNueva: () => void }) {
  const qc = useQueryClient();
  const openTab = useTabsStore((s) => s.openTab);
  const [filtro, setFiltro] = useState('todas');
  const { data: docs = [], isLoading } = useQuery<CotRow[]>({
    queryKey: ['cot', filtro],
    queryFn: async () => (await apiClient.post('/rpc/cot_listar', { _estado: filtro }, FIN)).data || [],
  });
  const { data: resumen } = useQuery<Resumen>({
    queryKey: ['cot-resumen'], queryFn: async () => (await apiClient.post('/rpc/cot_resumen', {}, FIN)).data,
  });
  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['cot'] });
    void qc.invalidateQueries({ queryKey: ['cot-resumen'] });
    void qc.invalidateQueries({ queryKey: ['fac-facturas'] });
  };
  const ok = (por: string) => (r: { message?: string }) => { toast.success(r?.message || por); invalidar(); };
  const mal = (e: unknown) => toast.error(getErrorMessage(e));

  const estado = useMutation({
    mutationFn: async (b: { _id: string; _estado: string; _motivo?: string }) =>
      (await apiClient.post('/rpc/cot_estado', b, FIN)).data,
    onSuccess: ok('Estado actualizado'), onError: mal,
  });
  const prorrogar = useMutation({
    mutationFn: async (b: { _id: string; _dias: number }) =>
      (await apiClient.post('/rpc/cot_prorrogar', b, FIN)).data,
    onSuccess: ok('Validez ampliada'), onError: mal,
  });
  const eliminar = useMutation({
    mutationFn: async (b: { _id: string }) =>
      (await apiClient.post('/rpc/cot_eliminar', b, FIN)).data,
    onSuccess: ok('Cotización eliminada'), onError: mal,
  });
  const convertir = useMutation({
    mutationFn: async (b: { _id: string }) =>
      (await apiClient.post('/rpc/cot_convertir_factura', b, FIN)).data,
    onSuccess: ok('Convertida en factura'), onError: mal,
  });

  const tot = docs.reduce((s, d) => s + Number(d.importe_total), 0);
  const dlg = useDialogoEnLinea();
  if (isLoading) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      {dlg.ui}
      {resumen && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {([
            ['Abiertas', resumen.abiertas, usd(Number(resumen.monto_abierto)), ''],
            ['Vencidas', resumen.vencidas, 'requieren prórroga', resumen.vencidas > 0 ? 'text-amber-600' : ''],
            ['Facturadas', resumen.convertidas, usd(Number(resumen.monto_convertido)), 'text-violet-600'],
            ['Efectividad', resumen.efectividad == null ? '—' : `${resumen.efectividad}%`, 'de las respondidas', ''],
          ] as const).map(([t, v, sub, cls]) => (
            <Card key={t}><CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{t}</p>
              <p className={`text-2xl font-semibold tabular-nums ${cls}`}>{v}</p>
              <p className="text-xs text-muted-foreground">{sub}</p>
            </CardContent></Card>
          ))}
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base">Cotizaciones ({docs.length})</CardTitle>
          <div className="flex items-center gap-2">
            <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={filtro} onChange={(e) => setFiltro(e.target.value)}>
              <option value="todas">Todas</option>
              <option value="abiertas">Abiertas</option>
              <option value="vencidas">Vencidas</option>
              <option value="aceptada">Aceptadas</option>
              <option value="rechazada">Rechazadas</option>
              <option value="convertida">Facturadas</option>
            </select>
            <Button size="sm" onClick={onNueva}><Plus className="w-4 h-4 mr-1" /> Nueva</Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {docs.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Sin cotizaciones en este filtro.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr>
                  <th className="py-2 pr-3 text-left w-10">N°</th>
                  <th className="py-2 pr-3 text-left">Número</th>
                  <th className="py-2 pr-3 text-left">Fecha</th>
                  <th className="py-2 pr-3 text-left">Válida hasta</th>
                  <th className="py-2 pr-3 text-left">Cliente</th>
                  <th className="py-2 pr-3 text-right">Total</th>
                  <th className="py-2 pr-3 text-left">Estado</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {docs.map((d, i) => (
                  <tr key={d.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{d.numero}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{String(d.fecha).slice(0, 10)}</td>
                    <td className={`py-2 pr-3 whitespace-nowrap ${d.vencida ? 'text-amber-600 font-medium' : ''}`}>
                      {String(d.fecha_validez).slice(0, 10)}{d.vencida ? ' ⚠' : ''}
                    </td>
                    <td className="py-2 pr-3">
                      {d.razon_social}
                      <span className="block text-xs text-muted-foreground">{d.identificacion}</span>
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums font-medium">{Number(d.importe_total).toFixed(2)}</td>
                    <td className="py-2 pr-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${(ESTADO[d.estado] || ESTADO.borrador).cls}`}>
                        {(ESTADO[d.estado] || ESTADO.borrador).label}
                      </span>
                      {d.estado === 'rechazada' && d.motivo_rechazo && (
                        <span className="mt-1 block max-w-xs text-xs text-muted-foreground" title={d.motivo_rechazo}>
                          {d.motivo_rechazo.length > 60 ? d.motivo_rechazo.slice(0, 60) + '…' : d.motivo_rechazo}
                        </span>
                      )}
                      {d.estado === 'convertida' && (
                        <button className="mt-1 block text-xs text-primary hover:underline"
                          onClick={() => openTab({ id: '/cxc/facturacion', title: 'Emitir Factura', path: '/cxc/facturacion' })}>
                          {d.factura_serie || 'factura por emitir'} →
                        </button>
                      )}
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle"
                        onClick={() => { void abrirCotizacionPdf(d.id).catch((e) => toast.error(getErrorMessage(e))); }}>
                        <FileText className="w-4 h-4" /> PDF
                      </button>
                      {d.estado !== 'convertida' && (
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted mr-1 align-middle"
                          title="Editar" onClick={() => onEdit(d.id)}><Pencil className="w-4 h-4" /></button>
                      )}
                      {d.estado === 'borrador' && (
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted mr-1 align-middle text-blue-600"
                          title="Marcar como enviada al cliente"
                          onClick={() => estado.mutate({ _id: d.id, _estado: 'enviada' })}><Send className="w-4 h-4" /></button>
                      )}
                      {d.estado === 'enviada' && (
                        <>
                          <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted mr-1 align-middle text-green-600"
                            title="El cliente la aceptó"
                            onClick={() => estado.mutate({ _id: d.id, _estado: 'aceptada' })}><Check className="w-4 h-4" /></button>
                          <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted mr-1 align-middle text-red-600"
                            title="El cliente la rechazó"
                            onClick={async () => {
                              const v = await dlg.pedir('Motivo del rechazo', [{ key: 'motivo', label: '¿Por qué la rechazaron?', placeholder: 'Precio, plazo, competencia…', minimo: 1 }]);
                              if (v) estado.mutate({ _id: d.id, _estado: 'rechazada', _motivo: v.motivo.trim() });
                            }}><X className="w-4 h-4" /></button>
                        </>
                      )}
                      {d.vencida && d.estado !== 'convertida' && (
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted mr-1 align-middle text-amber-600"
                          title="Ampliar la validez"
                          onClick={async () => {
                            const v = await dlg.pedir('Ampliar validez', [{ key: 'dias', label: '¿Cuántos días más de validez?', tipo: 'number', valor: '15', minimo: 1 }]);
                            const n = Number(v?.dias);
                            if (n > 0) prorrogar.mutate({ _id: d.id, _dias: n });
                          }}><CalendarPlus className="w-4 h-4" /></button>
                      )}
                      {['enviada', 'aceptada'].includes(d.estado) && !d.vencida && (
                        <Button size="sm" isLoading={convertir.isPending}
                          onClick={async () => { if (await dlg.confirmar(`¿Convertir ${d.numero} en factura?`, { detalle: 'La factura quedará POR EMITIR: podrás revisarla y emitirla desde Emitir Factura.' })) convertir.mutate({ _id: d.id }); }}>
                          <ArrowRightCircle className="w-3.5 h-3.5 mr-1" /> Facturar
                        </Button>
                      )}
                      {d.estado !== 'convertida' && (
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1 align-middle"
                          title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar ${d.numero}?`, { peligro: true })) eliminar.mutate({ _id: d.id }); }}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t font-medium">
                <tr>
                  <td colSpan={5} className="py-2 pr-3 text-right text-muted-foreground">Total</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{usd(tot)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default Cotizaciones;
